import { useState, useEffect } from 'react'
import Input from 'components/atoms/Input'
import Switch from 'components/atoms/Switch'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import { trim } from 'lodash'
import { useTranslation } from 'react-i18next'

const AddInsuranceModal = ({ onSuccess, close, id = null, close: customClose }) => {
  const { t } = useTranslation()

  const [formData, setFormData] = useState({
    insurance: '',
    insuranceAr: '',
    active: true,
  })

  useEffect(() => {
    if (id) {
      fetchInsuranceData()
    }
  }, [id])

  const fetchInsuranceData = async () => {
    try {
      const response = await apiGet(`insurance/${id}`)
      if (response.success) {
        const data = response.data
        setFormData({
          insurance: data.insurance || '',
          insuranceAr: data.insuranceAr || '',
          active: data.active !== undefined ? data.active : true,
        })
      } else {
        notify.error(t('addInsuranceModal.messages.failedToFetchInsuranceData'))
      }
    } catch (error) {
      console.error('Error fetching insurance data:', error)
      notify.error(t('addInsuranceModal.messages.failedToFetchInsuranceData'))
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleSwitchChange = checked => {
    setFormData(prevData => ({
      ...prevData,
      active: checked,
    }))
  }

  const handleSubmit = async () => {
    if (!trim(formData.insurance)) {
      notify.error(t('addInsuranceModal.messages.insuranceNameRequired'))
      return
    }
    if (!trim(formData.insuranceAr)) {
      notify.error(t('addInsuranceModal.messages.insuranceArabicNameRequired'))
      return
    }

    try {
      let response
      if (id) {
        response = await apiPut(`insurance/${id}`, formData)
        if (response.success) {
          notify.success(t('addInsuranceModal.messages.insuranceUpdatedSuccessfully'))
        }
      } else {
        response = await apiPost('insurance', formData)
        if (response.success) {
          notify.success(t('addInsuranceModal.messages.insuranceAddedSuccessfully'))
        }
      }

      if (response.success) {
        onSuccess()
        if (customClose) customClose()
        close()
      } else {
        notify.error(response.message || t('addInsuranceModal.messages.operationFailed'))
      }
    } catch (error) {
      console.error('Error saving insurance:', error)
      notify.error(t('addInsuranceModal.messages.failedToSaveInsurance'))
    }
  }

  const handleCancel = () => {
    if (customClose) customClose()
    close()
  }

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="insurance" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addInsuranceModal.labels.insurance')}
        </label>
        <input
          type="text"
          name="insurance"
          value={formData.insurance}
          onChange={handleChange}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addInsuranceModal.placeholders.enterInsurance')}
        />
      </div>

      <div>
        <label htmlFor="insuranceAr" className="block text-sm font-medium text-gray-700 mb-2">
          {t('addInsuranceModal.labels.insuranceArabic')}
        </label>
        <input
          type="text"
          name="insuranceAr"
          value={formData.insuranceAr}
          onChange={handleChange}
          dir="rtl"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          placeholder={t('addInsuranceModal.placeholders.enterInsuranceArabic')}
        />
      </div>

      <div className="flex items-center justify-between">
        <label htmlFor="active" className="block text-sm font-medium text-gray-700">
          {t('addInsuranceModal.labels.active')}
        </label>
        <Switch value={formData.active} onChange={handleSwitchChange} />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          onClick={handleCancel}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('addInsuranceModal.buttons.cancel')}
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
        >
          {id ? t('addInsuranceModal.buttons.updateInsurance') : t('addInsuranceModal.buttons.addInsurance')}
        </button>
      </div>
    </div>
  )
}

export default AddInsuranceModal
